import { getGlobalLookup } from "./lookup";
import { getAllOverriddenGlobalProperties } from "./override";

const loggedOverrides = new Set<string>();

const logOverrideOnce = (key: string, value: unknown): void => {
  if (
    typeof process === "undefined" ||
    process.env.NODE_ENV === "test" ||
    loggedOverrides.has(key)
  ) {
    return;
  }
  loggedOverrides.add(key);
  console.log(
    `Using global property override for ${key} of ${JSON.stringify(value)}`
  );
};

/**
 * Gets the given property name from the global page data.
 * Note that it supports nested/chained properties, e.g. "foo.bar".
 * @deprecated do not use global properties if you can avoid it. Either prop drill,
 * or if in react use context or a state management library.
 *
 * If you ABSOLUTELY MUST use a global property, use defineGlobal from faire/globals instead.
 */
export function getGlobalProperty<T>(name: string): T | undefined;
export function getGlobalProperty<T>(name: string, defaultValue: T): T;
export function getGlobalProperty<T>(
  name: string,
  defaultValue?: T
): T | undefined {
  const allOverriddenPageData = getAllOverriddenGlobalProperties();
  // Check if direct reference is mocked out
  if (name in allOverriddenPageData) {
    const overrideValue = allOverriddenPageData[name];
    logOverrideOnce(name, overrideValue);
    return overrideValue ?? defaultValue;
  }

  const overrideProperties: Partial<T> = {};
  const allOverriddenPageDataKeys = Object.keys(allOverriddenPageData);
  for (const key of allOverriddenPageDataKeys) {
    const [parent, child] = key.split(".");
    // Check if child reference is mocked out
    if (name === parent && child !== undefined) {
      const overrideValue = allOverriddenPageData[key];
      logOverrideOnce(key, overrideValue);

      overrideProperties[child as keyof T] = overrideValue;
    }
  }

  const value: T | undefined = getGlobalLookup()(name) ?? defaultValue;

  if (
    typeof process !== "undefined" &&
    process.env.NEXT_PUBLIC_DEBUG_GLOBAL_LOOKUP &&
    !globalsToSkip.includes(name)
  ) {
    console.log(
      `Global Lookup: ${name}`,
      new Error().stack?.split("\n").slice(1, 6)
    );
  }

  if (
    typeof value === "object" &&
    Object.keys(overrideProperties).length !== 0
  ) {
    return { ...value, ...overrideProperties };
  }

  return value;
}

// Globals that we don't want to log - they are either expected (like window)
// or are too noisy to give any insight.
const globalsToSkip = [
  "window",
  "document",
  "location",
  "navigator",
  "needsHydration",
  "envName",
  "faire.app",
  "isRobot",
  "isPrerender",
  "settingsValues",
  "user",
  "retailer",
];
